// Input sanitization utility
export const sanitizePrompt = (prompt: string): string => {
  if (!prompt) return '';
  
  // Remove HTML/XML tags
  const htmlCleaned = prompt.replace(/<[^>]*>/g, '');
  
  // Encode special characters
  const encoded = htmlCleaned.replace(/[<>"'&]/g, (char) => {
    const charMap: { [key: string]: string } = {
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#x27;',
      '&': '&amp;'
    };
    return charMap[char];
  });
  
  // Remove excessive whitespace
  return encoded.replace(/\s+/g, ' ').trim();
};

// Enhanced prompt injection detection
const detectPromptInjection = (prompt: string): { detected: boolean; reason?: string } => {
  const lowerPrompt = prompt.toLowerCase();
  
  // System instruction override attempts
  const systemPromptPatterns = [
    /ignore\s+(previous|all|above|prior|earlier)\s+instructions?/gi,
    /forget\s+(everything|all|previous|prior|earlier)/gi,
    /\bnow\s+(do|act|behave|respond)\s+as/gi,
    /you\s+are\s+(now|no\s+longer)/gi,
    /override\s+your\s+(instructions|system|prompt)/gi,
    /disregard\s+(previous|all|above|prior)/gi,
    /instead\s+of\s+finding\s+highlights/gi,
    /don't\s+(find|detect|look)\s+(highlights|clips)/gi
  ];
  
  for (const pattern of systemPromptPatterns) {
    if (pattern.test(prompt)) {
      return { detected: true, reason: 'Prompt injection attempt detected' };
    }
  }
  
  // Role confusion attempts
  const roleConfusionPatterns = [
    'you are a', 'act as', 'pretend to be', 'roleplay as',
    'simulate being', 'behave like', 'respond as if you are'
  ];
  
  if (roleConfusionPatterns.some(pattern => lowerPrompt.includes(pattern))) {
    return { detected: true, reason: 'Role confusion attempt detected' };
  }
  
  return { detected: false };
};

// Video highlight content validation
const validateVideoContent = (prompt: string): { isValid: boolean; reason?: string } => {
  const lowerPrompt = prompt.toLowerCase();
  
  // Allowed video/gaming related terms
  const allowedVideoTerms = [
    'highlight', 'clip', 'moment', 'action', 'goal', 'save', 'best',
    'funny', 'epic', 'intense', 'exciting', 'reaction', 'gameplay',
    'kill', 'death', 'win', 'lose', 'victory', 'defeat', 'play',
    'game', 'match', 'round', 'shot', 'move', 'skill', 'trick',
    'fail', 'success', 'amazing', 'incredible', 'good', 'bad',
    'stream', 'twitch', 'vod', 'video', 'content', 'chat',
    'viewer', 'audience', 'entertainment', 'fun', 'comedy'
  ];
  
  // Check if prompt contains at least one valid term (for prompts > 20 chars)
  if (prompt.length > 20) {
    const hasValidContent = allowedVideoTerms.some(term => 
      lowerPrompt.includes(term)
    );
    
    if (!hasValidContent) {
      return { 
        isValid: false, 
        reason: 'Prompt must be related to video highlights or gaming content' 
      };
    }
  }
  
  return { isValid: true };
};

// Helper function to validate LLM prompt content on the client-side
export const validateLlmPromptClientSide = (prompt: string): { isValid: boolean; reason?: string } => {
  if (!prompt) {
    return { isValid: true }; // Empty prompt is valid for client-side
  }

  // Sanitize input first
  const sanitized = sanitizePrompt(prompt);
  if (sanitized !== prompt) {
    return { isValid: false, reason: 'Prompt contains potentially unsafe characters' };
  }

  // 1. Prompt injection detection
  const injectionCheck = detectPromptInjection(prompt);
  if (injectionCheck.detected) {
    return { isValid: false, reason: injectionCheck.reason };
  }

  // 2. Video content validation
  const contentCheck = validateVideoContent(prompt);
  if (!contentCheck.isValid) {
    return contentCheck;
  }

  // 3. URL Detection
  const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/gi;
  if (urlRegex.test(prompt)) {
    return { isValid: false, reason: 'URLs are not allowed in prompts' };
  }

  // 4. Code/Script detection
  const codePatterns = [
    /```[\s\S]*?```/g,
    /<script\b[^>]*>[\s\S]*?<\/script>/gi,
    /\b(function|class|const|let|var|import|require|eval)\b/gi,
    /[{}();]/g
  ];
  
  const scriptKeywords = [
    'javascript:', 'vbscript:', 'onmouseover=', 'onerror=', 'onload=',
    'onclick=', 'onsubmit=', 'onchange='
  ];
  
  if (codePatterns.some(pattern => pattern.test(prompt)) || 
      scriptKeywords.some(keyword => prompt.toLowerCase().includes(keyword))) {
    return { isValid: false, reason: 'Code or script content is not allowed' };
  }

  // 5. SQL injection detection
  const sqlKeywords = [
    'select from', 'insert into', 'update set', 'delete from', 
    'drop table', 'alter table', 'union select', 'xp_cmdshell', 
    '--', '/*', '*/'
  ];
  
  if (sqlKeywords.some(keyword => prompt.toLowerCase().includes(keyword))) {
    return { isValid: false, reason: 'SQL-like content is not allowed' };
  }

  // 6. Command injection detection
  const commandKeywords = [
    'rm -rf', 'mkfs', 'shutdown', 'reboot', 'wget', 'curl', 
    'nc ', 'netcat', '| bash', '&& bash', '; bash', 
    'powershell', 'cmd.exe', 'sudo ', 'chmod '
  ];
  
  if (commandKeywords.some(keyword => prompt.toLowerCase().includes(keyword))) {
    return { isValid: false, reason: 'System commands are not allowed' };
  }

  // 7. Prohibited content
  const prohibitedKeywords = [
    'bitcoin', 'crypto', 'hack', 'password', 'exploit', 'malware', 'virus', 
    'porn', 'adult', 'illegal', 'crack', 'phishing', 'spam', 'ddos', 
    'botnet', 'rootkit', 'keylogger', 'trojan', 'warez', 'carding',
    'hikaye yaz', 'metni çevir', 'translate', 'story', 'essay'
  ];
  
  if (prohibitedKeywords.some(keyword => prompt.toLowerCase().includes(keyword))) {
    return { isValid: false, reason: 'Prompt contains prohibited content' };
  }

  return { isValid: true };
};